import type { FetchLike } from "@medplum/core";
import type { OperationOutcome, OperationOutcomeIssue } from "@medplum/fhirtypes";
import { RetryAfterError } from "inngest";
import {
  containsPostgresSerializationError,
  containsTransientErrorMessage,
  isTransientMedplumNetworkError,
} from "@/lib/errors/medplum-transient";
import { createLogger } from "@/lib/logger";
import { extractFetchUrlPath } from "@/lib/medplum/extract-fetch-url-path";
import {
  callWithSentrySpan,
  reportRateLimitStatusFromResponse,
} from "@/lib/medplum/report-fhir-interactions-quota-gauge";
import { logMedplumResponse } from "@/lib/medplum/response-telemetry";
import { timeAmount } from "@/lib/time";
import { parseRetryAfterMs, withRetry } from "@/lib/utils/retry";

const DEFAULT_DELAY = timeAmount({ seconds: 2 });
const DEFAULT_MAX_RETRIES = 7;

/**
 * Default threshold (ms) above which a rate-limit wait is escalated to Inngest
 * instead of sleeping in-process. Keeps short waits fast while offloading long
 * ones so they don't burn Vercel invocation time.
 */
const DEFAULT_DURABLE_THRESHOLD_MS = timeAmount({ seconds: 2 }).asMilliseconds();

/** Where the retry delay hint came from. */
export type ThrottleSource = "_msBeforeNext" | "retry-after" | "backoff";

/**
 * Where Medplum rate-limit waits are handled:
 * - `"local"`: sleep in-process (default for scripts, migrations, non-Inngest contexts)
 * - `"durable"`: once the Medplum client reads this flag, it will throw Inngest
 *                `RetryAfterError` so the wait is durably scheduled and doesn't
 *                burn Vercel invocation time (wired in a follow-up PR)
 */
export type ThrottleStrategy = "local" | "durable";

/**
 * Error thrown when a fetch response has a retryable status code.
 * Carries the original Response so it can be returned when retries are exhausted.
 */
class FetchRetryError extends Error {
  readonly response: Response;
  retryAfterMs?: number;
  source?: ThrottleSource;

  constructor({
    response,
    retryAfterMs,
    source,
  }: {
    response: Response;
    retryAfterMs?: number;
    source?: ThrottleSource;
  }) {
    super(`Retryable HTTP ${response.status}`);
    this.name = "FetchRetryError";
    this.response = response;
    this.retryAfterMs = retryAfterMs;
    this.source = source;
  }
}

/**
 * Returns true for HTTP status codes that are transient and safe to retry.
 */
function isRetryableStatus(status: number): boolean {
  return status === 429 || status === 408 || status >= 500;
}

/**
 * Detects whether an OperationOutcome issue indicates a rate limit condition.
 */
function isRateLimitOutcomeIssue(issue: OperationOutcomeIssue): boolean {
  const diagnostics = issue.diagnostics?.toLowerCase() ?? "";
  const detailText = issue.details?.text?.toLowerCase() ?? "";
  const code = issue.code?.toLowerCase() ?? "";

  return (
    diagnostics.includes("rate limit") ||
    diagnostics.includes("too many request") ||
    detailText.includes("rate limit") ||
    detailText.includes("too many request") ||
    code === "throttled"
  );
}

/**
 * Parses an _msBeforeNext hint from a string payload (e.g. OperationOutcome diagnostics).
 */
function parseMsBeforeNext(value: string | undefined): number | undefined {
  if (!value) {
    return undefined;
  }

  const match = value.match(/_msBeforeNext["']?\s*:\s*(\d+)/i);
  if (!match) {
    return undefined;
  }

  const parsed = Number(match[1]);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * Adds 0-100% positive jitter to avoid thundering-herd retries.
 */
function applyJitter(delayMs: number): number {
  if (!Number.isFinite(delayMs) || delayMs <= 0) {
    return 0;
  }

  const jitterRatio = Math.random(); // 0.0 - 1.0
  return Math.round(delayMs * (1 + jitterRatio));
}

/**
 * Analyzes a response to determine if it should be retried and extracts retry timing hints.
 * Returns retry metadata if the response indicates a transient error.
 */
async function analyzeResponseForRetry(
  response: Response,
): Promise<{ shouldRetry: boolean; retryAfterMs?: number; source?: ThrottleSource }> {
  // Never retry 2xx success responses
  if (response.status >= 200 && response.status < 300) {
    return { shouldRetry: false };
  }

  const cloned = response.clone();
  let retryAfterMs: number | undefined;
  let source: ThrottleSource | undefined;
  let shouldRetry = false;

  // First priority: Check if status code is retryable
  if (isRetryableStatus(response.status)) {
    shouldRetry = true;
  }

  // Check Retry-After header (RFC 9110) — use as timing hint only.
  // The header alone does NOT force a retry; the status code or body
  // must independently indicate a transient/retryable condition.
  const retryAfterHeader = response.headers.get("Retry-After") ?? undefined;
  const headerMs = parseRetryAfterMs(retryAfterHeader);
  if (headerMs !== undefined) {
    retryAfterMs = applyJitter(headerMs);
    source = "retry-after";
  }

  // Analyze response body for additional retry signals and timing hints
  try {
    const text = await cloned.text();

    // Try to parse as JSON to check OperationOutcome
    try {
      const body = JSON.parse(text);

      // Check if this is an OperationOutcome with rate limit indicators
      if (body?.resourceType === "OperationOutcome" && Array.isArray(body.issue)) {
        const outcome = body as OperationOutcome;

        const isRateLimit = outcome.issue.some(isRateLimitOutcomeIssue);
        if (isRateLimit) {
          shouldRetry = true;
        }

        // Extract _msBeforeNext if present (takes precedence over Retry-After
        // b/c it's Medplum-specific and more granular)
        if (isRateLimit || shouldRetry) {
          for (const issue of outcome.issue) {
            const value =
              parseMsBeforeNext(issue.diagnostics) ?? parseMsBeforeNext(issue.details?.text);
            if (value !== undefined) {
              retryAfterMs = applyJitter(value);
              source = "_msBeforeNext";
              break;
            }
          }
        }
      }
    } catch {
      // Not JSON, check raw text
    }

    // Check for PostgreSQL serialization errors in response text (retry regardless of status)
    if (containsPostgresSerializationError(text)) {
      shouldRetry = true;
    }

    // Check for generic transient error messages (retry regardless of status)
    if (containsTransientErrorMessage(text)) {
      shouldRetry = true;
    }
  } catch {}

  return { shouldRetry, retryAfterMs, source };
}

/**
 * Checks if a network error (non-HTTP error) should be retried based on message patterns.
 * This matches the OLD application-layer retry behavior for errors thrown by fetch().
 */
function shouldRetryNetworkError(error: unknown): boolean {
  // An abort is the caller withdrawing, not a transient fault. Retrying it would re-issue a
  // request nobody is waiting for and keep the loop alive past the budget that stopped it —
  // and "aborted" reads as a connection error to `isTransientMedplumNetworkError`, so this guard
  // has to come first rather than rely on it.
  if (isAbortError(error)) return false;

  return isTransientMedplumNetworkError(error);
}

/**
 * True for a cancellation, whichever name it arrives under.
 *
 * `TimeoutError` is not optional here: `AbortSignal.timeout()` rejects with THAT, not
 * `AbortError`, and its message ("aborted due to timeout") matches the substring checks in
 * `isTransientMedplumNetworkError` — so omitting it means every ceiling this module sets gets
 * retried seven more times, which is the opposite of a ceiling.
 */
export function isAbortError(error: unknown): boolean {
  const name = errorName(error);
  return name === "AbortError" || name === "TimeoutError";
}

function errorName(error: unknown): string {
  return error instanceof Error || error instanceof DOMException ? error.name : "";
}

interface RetryFetchOptions {
  /**
   * Custom fetch implementation for testing. Defaults to global fetch.
   */
  fetchFn?: FetchLike;

  /**
   * Label used to scope the logger instance (e.g. "user", "background", "serverside").
   * Produces log lines prefixed with "medplum/{label}".
   */
  label?: string;

  /**
   * When set to `"durable"`, rate-limit waits that exceed `durableThresholdMs`
   * throw Inngest `RetryAfterError` instead of sleeping in-process. This hands
   * the wait off to Inngest's durable scheduler so long backoffs don't consume
   * Vercel invocation time.
   *
   * Defaults to `undefined` (all retries handled locally).
   */
  throttling?: "durable";

  /**
   * Bounds this client's work: aborts the in-flight request AND the retry loop's backoff
   * sleep, and stops further attempts.
   *
   * The Medplum client exposes no per-call signal, so a caller that needs a hard upper bound
   * supplies one here at construction and every request the client makes inherits it. That is
   * the whole mechanism behind the drain's per-practice budget — without it, seven retries
   * honoring a server `Retry-After` can hold a background task open for minutes with nothing
   * able to interrupt it.
   *
   * Omitted by default. Ordinary API request-path clients pass nothing and keep their exact
   * current retry behavior.
   */
  signal?: AbortSignal;

  /**
   * Hard ceiling on one CALL — the whole retry loop, not one attempt — minted fresh each time
   * this fetch is invoked.
   *
   * Bounding a single attempt would not be a ceiling at all: eight attempts plus exponential
   * backoff (2s, 4s … 128s) run for minutes regardless of what any one of them is allowed.
   * So the minted signal is passed to `withRetry` as well as to each attempt's `init`.
   *
   * Distinct from `signal`, which is one budget shared across every request a client makes.
   * A long-lived client that needs "no single call may hang forever" wants this; a short-lived
   * one bounding its total work wants `signal`. They compose.
   */
  timeoutMs?: number;

  /**
   * Minimum retry-after delay (ms) before escalating to Inngest. Delays below
   * this threshold are still handled in-process since an Inngest checkpoint is
   * more expensive than a short sleep.
   *
   * Only meaningful when `throttling` is `"durable"`. Defaults to 2 000 ms.
   */
  durableThresholdMs?: number;
}

/**
 * Creates a fetch-compatible function that automatically retries on transient HTTP errors.
 *
 * Wraps the global `fetch` with retry logic for:
 * - HTTP status codes: 429, 408, and 5xx
 * - OperationOutcome issues indicating rate limits or throttling
 * - PostgreSQL serialization errors (deadlock, serialization failure)
 * - Generic transient error messages (rate limit, timeout)
 * - Network errors with matching message patterns (preserves OLD behavior)
 *
 * Uses exponential backoff with jitter and respects Medplum's _msBeforeNext hints.
 */
/**
 * Merges a client-level budget signal into one request's init without discarding a signal the
 * caller already set. Returns `init` untouched when there is nothing to add, so the no-signal
 * path allocates nothing and behaves exactly as before.
 */
function withSignal(init: RequestInit | undefined, signal: AbortSignal | undefined): RequestInit {
  const composed = composeSignals([init?.signal, signal]);
  return composed ? { ...init, signal: composed } : (init ?? {});
}

/** One signal from however many are present, or undefined when none are. */
function composeSignals(candidates: (AbortSignal | null | undefined)[]): AbortSignal | undefined {
  const present = candidates.filter((candidate): candidate is AbortSignal => !!candidate);
  if (!present.length) return undefined;
  return present.length === 1 ? present[0] : AbortSignal.any(present);
}

export function createRetryFetch(options?: RetryFetchOptions): FetchLike {
  const fetchFn = options?.fetchFn ?? globalThis.fetch;
  const logger = options?.label ? createLogger(`medplum/${options.label}`) : undefined;
  // response telemetry logs for every client, labelled or not
  const responseLabel = options?.label ?? "unlabeled";
  const responseLogger = logger ?? createLogger(`medplum/${responseLabel}`);
  const clientSignal = options?.signal;
  const timeoutMs = options?.timeoutMs;
  const durableThresholdMs =
    options?.throttling === "durable"
      ? (options.durableThresholdMs ?? DEFAULT_DURABLE_THRESHOLD_MS)
      : undefined;

  return async (url: string, init?: RequestInit) => {
    // Minted per CALL and shared by every attempt and sleep below, so the ceiling covers the
    // whole loop. A signal minted per attempt would reset on each retry and bound nothing.
    const callTimeout = timeoutMs ? AbortSignal.timeout(timeoutMs) : undefined;
    const boundedSignal = composeSignals([clientSignal, callTimeout]);
    const startMs = Date.now();
    let attempts = 0;
    let lastThrottleSource: ThrottleSource | undefined;

    try {
      const response = await withRetry(
        async () => {
          attempts++;
          const method = init?.method ?? "GET";
          const urlPath = extractFetchUrlPath(url);

          const resp = await callWithSentrySpan<Response>({
            name: `Medplum ${method} ${urlPath}`,
            callback: async (span) => {
              const attemptStartMs = Date.now();
              const logAttempt = (status: number | null, timedOut = false) =>
                logMedplumResponse({
                  logger: responseLogger,
                  label: responseLabel,
                  method,
                  urlPath,
                  status,
                  attemptMs: Date.now() - attemptStartMs,
                  elapsedMs: Date.now() - startMs,
                  attempt: attempts,
                  timedOut,
                });

              // Composed rather than overwritten: a caller's own signal still applies, and
              // the client-level budget is added to it. `AbortSignal.any` settles on whichever
              // fires first and is what makes the in-flight request genuinely cancellable.
              let resp: Response;
              try {
                resp = await (fetchFn as typeof fetch)(url, withSignal(init, boundedSignal));
              } catch (error) {
                if (!isAbortError(error)) {
                  logAttempt(null);
                } else if (callTimeout?.aborted) {
                  // our own timeoutMs fired - Medplum was slower than the budget, the strongest
                  // latency signal there is. Which signal fired decides, never the error name.
                  logAttempt(null, true);
                }
                // a caller's cancellation (client signal or per-request init.signal) is an
                // intended yield, not a Medplum fault - no line
                throw error;
              }
              // per attempt, before the retry decision - the end-of-call lines below only
              // carry the final status, so a 503 that a retry hid would vanish
              logAttempt(resp.status);

              // Always record Medplum rate-limit status from *every* response (success or failure).
              // This is intentionally before retry analysis so 429/5xx responses still contribute.
              reportRateLimitStatusFromResponse(resp, {
                label: options?.label,
                url,
                spanOverride: span,
              });
              return resp;
            },
          });

          const { shouldRetry, retryAfterMs, source } = await analyzeResponseForRetry(resp);

          if (!shouldRetry) {
            return resp;
          }

          // Track the most recent delay mechanism so telemetry reflects the last retry, not a stale earlier one
          lastThrottleSource = source ?? "backoff";

          // Escalate to Inngest durable wait when the delay exceeds the threshold.
          // RetryAfterError is NOT caught by shouldRetry below, so it propagates
          // out of withRetry and lets Inngest schedule the retry durably.
          if (
            durableThresholdMs !== undefined &&
            retryAfterMs !== undefined &&
            retryAfterMs > durableThresholdMs
          ) {
            logger?.warn("Escalating to durable retry", {
              method: init?.method ?? "GET",
              path: extractFetchUrlPath(url),
              status: resp.status,
              retryAfterMs,
              source: source ?? "backoff",
              handledBy: "inngest" as const,
            });
            throw new RetryAfterError(
              `Medplum ${resp.status}: retry-after ${retryAfterMs}ms exceeds threshold`,
              retryAfterMs,
            );
          }

          throw new FetchRetryError({ response: resp, retryAfterMs, source });
        },
        {
          shouldRetry: (error: unknown) => {
            // HTTP response errors wrapped in FetchRetryError
            if (error instanceof FetchRetryError) {
              return true;
            }

            // Network errors (fetch threw) - check message patterns to match OLD behavior
            const retry = shouldRetryNetworkError(error);
            if (retry) {
              // Network errors have no server hint — always backoff
              lastThrottleSource = "backoff";
            }
            return retry;
          },
          delay: DEFAULT_DELAY,
          randomJitter: true,
          maxRetries: DEFAULT_MAX_RETRIES,
          // Bounds the SLEEP too. Aborting only the request would still leave a long
          // `Retry-After` holding the loop for its full interval before anything noticed.
          // Covers the SLEEP as well as the attempts: a long `Retry-After` would otherwise
          // hold the loop past whichever ceiling stopped it.
          ...(boundedSignal ? { signal: boundedSignal } : {}),
        },
      );

      if (attempts > 1) {
        const durationMs = Date.now() - startMs;
        const meta = {
          method: init?.method ?? "GET",
          path: extractFetchUrlPath(url),
          status: response.status,
          attempts,
          durationMs,
          source: lastThrottleSource ?? ("backoff" as ThrottleSource),
          handledBy: "local" as const,
        };

        if (response.ok) {
          logger?.warn("Request succeeded after retries", meta);
        } else {
          logger?.error("Request returned error after retries", meta);
        }
      }

      return response;
    } catch (error) {
      const durationMs = Date.now() - startMs;
      const method = init?.method ?? "GET";
      const path = extractFetchUrlPath(url);

      // Durable escalation — already logged at warn level before the throw.
      // Re-throw without additional logging to avoid misleading error metrics.
      if (error instanceof RetryAfterError) {
        throw error;
      }

      // When retries are exhausted, return the last HTTP response so the SDK can parse its own errors
      if (error instanceof FetchRetryError) {
        logger?.error("Request failed after retries exhausted", {
          method,
          path,
          status: error.response.status,
          attempts,
          durationMs,
          source: lastThrottleSource ?? ("backoff" as ThrottleSource),
          handledBy: "local" as const,
        });
        return error.response;
      }

      // A cancellation is not a network fault, and this wrapper is shared by every
      // server-side Medplum call. Logging one at error level would make an intended yield
      // indistinguishable from a real outage — and the drain's per-practice budget aborts on
      // purpose, on every tick a busy practice runs long.
      if (isAbortError(error)) {
        const cancelled = { method, path, attempts, durationMs };
        // Which SIGNAL fired, never which name the error carries. A caller picks its own
        // abort reason and the drain budget deliberately uses `TimeoutError` for one, so
        // branching on the name reports the commonest intended yield as a ceiling breach.
        if (options?.signal?.aborted) {
          // The caller withdrew. It owns the reason and logs it itself, so a warn here
          // would double-report one event and train an operator to skim past both.
          logger?.debug("Request cancelled by caller", cancelled);
        } else {
          // No caller signal fired, so the only bound left is the `timeoutMs` THIS wrapper
          // set (the serving-identity lookup's 20s cap). Worth seeing: something downstream
          // is slower than a ceiling another path depends on.
          logger?.warn("Request exceeded its timeout", cancelled);
        }
        throw error;
      }

      logger?.error("Request failed with network error", {
        method,
        path,
        attempts,
        durationMs,
        error: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
  };
}

/**
 * Executes a Medplum operation directly without application-level retry.
 *
 * Retry logic is now handled at the fetch layer via `createRetryFetch()`, which is
 * injected into all server-side MedplumClient constructors. This function is kept as
 * a passthrough so existing call sites continue to compile.
 *
 * @deprecated Retry is handled by createRetryFetch(). Remove the wrapper at call sites.
 */
export function retryMedplumCall<T>(
  operation: () => Promise<T>,
  _options?: { maxRetries?: number },
): Promise<T> {
  return operation();
}
